import { ButtonType, CheckBoxWithLabelWidget, DialogFooterButtonsWidget, TabStyles } from '@tableau/widgets';
import React from 'react';
import { defaultMemoize } from 'reselect';

import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { getAppStore } from '~src/app/AppStore';
import { SessionUserSelectors } from '~src/app/selectors/SessionInfoSelectors';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { SubscriptionFlow, SubscriptionFlowMetadata } from '~src/ts/ServerApi/types/SubscriptionFlow';

import { Messages } from '../../codegen/Localize';
import { CommonStyles } from '../../common/react/CommonStyles';
import { IError } from '../../httpClient/serverApi/types/IError';
import { IResult } from '../../httpClient/serverApi/types/IResponse';
import { cssClass } from '../../style';
import { L10n } from '../../ts/L10n/L10n';
import { FlowBaseInfo } from '../../ts/models/FlowInfo';
import { FlowOutputStepInfo } from '../../ts/models/FlowOutputStepInfo';
import { RunFlowTaskInfo } from '../../ts/models/RunFlowTaskInfo';
import { IFlowRunSpecOutputStepViewModel } from '../../ts/ServerApi/types/IFlowRunSpecOutputStepViewModel';
import { ToasterService, ToastType } from '../../ts/services/ToasterService';
import { Flows } from '../resources/Flows';
import { RunFlowTasks } from '../resources/RunFlowTasks';
import { FlowOutputsSelection } from './FlowOutputsSelection';
import { FlowRunSchedulePicker } from './FlowRunSchedulePicker';
import { FlowSubscriptionButtonWithDialog } from './FlowSubscriptionButtonWithDialog';

const cssPrefix = 'FlowRunTaskDialogContent';

const outerMargin = cssClass(cssPrefix, 'outerMargin', {
  display: 'flex',
  flexDirection: 'column',
  margin: `${CommonStyles.Sizes.LargeSpace}px`,
});

const scrollWrapper = cssClass(cssPrefix, 'scrollWrapper', {
  height: 385,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
});

const rootClass = cssClass(cssPrefix, 'rootClass', {
  padding: `0 ${CommonStyles.Sizes.SmallSpace}px`
});

const dialogMessage = cssClass(cssPrefix, 'dialogMessage', {
  marginBottom: `${TabStyles.Spacing.LargeSpace}px`,
  wordWrap: 'break-word'
});

const footerWrapper = cssClass(cssPrefix, 'footerWrapper', {
  paddingTop: CommonStyles.Sizes.MediumSpace,
});

const emailWhenDoneWrapper = cssClass(cssPrefix, 'emailWhenDoneWrapper', {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
});

export type FlowRunTaskDialogContentProps = {
  taskInfo?: RunFlowTaskInfo; // Used for editing a task
  flowInfo?: FlowBaseInfo;    // Used for creating a task
  singleSelectedOutputId?: string; // Used for creating a task starting with a single selected output
  dismiss: () => void;
};

export type FlowRunTaskDialogContentState = {
  selectedSchedule: string;
  selectAll: boolean;
  outputsSelected: { readonly [key: string]: boolean };
  outputsIsIncremental: { readonly [key: string]: boolean };
  isRunAllIncremental: boolean;
  outputs: ReadonlyArray<FlowOutputStepInfo>;
  isRunning: boolean;
  shouldSendEmailWhenDone: boolean;
  shouldSendToMe: boolean;
  subjectValue: string;
  messageValue: string;
};

export class FlowRunTaskDialogContent extends React.PureComponent<FlowRunTaskDialogContentProps, FlowRunTaskDialogContentState> {

  private toasterService: ToasterService;

  constructor(props: FlowRunTaskDialogContentProps) {
    super(props);
    this.toasterService = ToasterService.instance;
    const outputsSelected: {[key: string]: boolean} = {};
    const outputsIsIncremental: {[key: string]: boolean} = {};
    this.state = {
      outputs: null,
      selectedSchedule: this.props.taskInfo ? this.props.taskInfo.schedule.id : '',
      selectAll: false,
      outputsSelected: outputsSelected,
      outputsIsIncremental: outputsIsIncremental,
      isRunAllIncremental: null,
      isRunning: false,
      shouldSendEmailWhenDone: false,
      shouldSendToMe: true,
      subjectValue: Messages.flowSubscriptionsSubjectDefault({FLOW_NAME: this.props.flowInfo?.name}),
      messageValue: '',
    };
    Flows.getOutputStepInfos(this.props.flowInfo ? this.props.flowInfo.id : this.props.taskInfo.flow.id).then(items => {
      if (this.props.taskInfo) {
        if (this.props.taskInfo.runAllOutputs) {
          this.setState({
            outputs: items,
            selectAll: true,
            outputsSelected: this.getAllOutputsSelected(items),
            isRunAllIncremental: this.props.taskInfo.isRunAllIncremental,
          });

        } else {
          const existingSelectedOutputs: {[key: string]: boolean} = {};
          const existingOutputsIsIncremental: {[key: string]: boolean} = {};
          this.props.taskInfo.flowOutputSteps.forEach((step) => {
            existingSelectedOutputs[step.id] = true;
            existingOutputsIsIncremental[step.id] = step.isIncremental;
          });
          this.setState({
            outputs: items,
            outputsSelected: existingSelectedOutputs,
            outputsIsIncremental: existingOutputsIsIncremental
          });
        }
      } else {
        // by default we want to include all outputs
        // in the case we are opening from the overview page, we have a single output preselected
        this.setState({
          outputs: items,
          selectAll: !this.props.singleSelectedOutputId,
          outputsSelected: this.props.singleSelectedOutputId ?
            {[this.props.singleSelectedOutputId]: true}
            : this.getAllOutputsSelected(items)
        });
      }
    });
}

  private onSelectSchedule = (value: string) => {
    this.setState({selectedSchedule: value});
  }

  private getAllOutputsSelected = (outputs: ReadonlyArray<FlowOutputStepInfo>) => {
    const outputsSelected: {[key: string]: boolean} = {};
    outputs.forEach(output => outputsSelected[output.id] = true);
    return outputsSelected;
  }

  private handleIncludeAll = () => {
    if (this.state.selectAll) {
      this.setState({
        selectAll: false,
        outputsSelected: {}
      });
    } else {
      this.setState({
        selectAll: true,
        outputsSelected: this.getAllOutputsSelected(this.state.outputs)
      });
    }
  }

  private toggleSelectAll = () => {
    if (this.getSelectedOutputsCount() === this.state.outputs.length) {
      this.setState({
        outputsSelected: {}
      });
    } else {
      this.setState({
        outputsSelected: this.getAllOutputsSelected(this.state.outputs)
      });
    }
  }

  private handleOutputChange = (id: string) => {
    this.setState({
      outputsSelected: {
        ...this.state.outputsSelected,
        [id]: !this.state.outputsSelected[id]
      }
    });
  }

  private handleOutputIsIncrementalChange = (id: string, isIncremental: boolean) => {
    if (this.state.selectAll) {
      this.setState({
        isRunAllIncremental: isIncremental,
      });
    } else {
      this.setState({
        outputsIsIncremental: {
          ...this.state.outputsIsIncremental,
          [id]: isIncremental
        },
      });
    }
  }

  private handleEmailWhenDoneToggled = () => {
    this.setState({shouldSendEmailWhenDone: !this.state.shouldSendEmailWhenDone});
  }

  private getFlowAndOuputStepParams(){
    const outputIds: string[] = [];
    const flowRunSpecOutputStepParams: IFlowRunSpecOutputStepViewModel[] = [];
    const isRunAllIncremental = this.state.isRunAllIncremental;

    for (const output in this.state.outputsSelected) {
      if (this.state.outputsSelected[output]) {
        outputIds.push(output);
        if (!this.state.selectAll) {
          flowRunSpecOutputStepParams.push({
            isIncremental: this.state.outputsIsIncremental[output],
            flowOutputStepId: parseInt(output, 10)
          });
        }
      }
    }

    return {outputIds, flowRunSpecOutputStepParams, isRunAllIncremental};
  }

  private getSubscriptionFlow = (): SubscriptionFlow => {
    const currentUserId = SessionUserSelectors.sessionUserId(getAppStore().getState());
    // TODO update userIds when we have user selector hooked up
    const userIds = this.state.shouldSendToMe ? [currentUserId] : [];
    const subscriptionFlowMetadata: SubscriptionFlowMetadata = {
      targetId: this.props.flowInfo.id,
      subject: this.state.subjectValue,
      message: this.state.messageValue,
      userIds: userIds
    };
    const subscriptionFlow: SubscriptionFlow = {
      subscriptionFlowMetadata: subscriptionFlowMetadata,
    };
    return subscriptionFlow;
  }

  private handleCreateSchedule = () => {
    this.setState({isRunning: true});
    const {outputIds, flowRunSpecOutputStepParams, isRunAllIncremental} = this.getFlowAndOuputStepParams();

    Flows.createRunFlowTask(
      this.props.flowInfo.id,
      this.state.selectedSchedule,
      this.state.selectAll,
      isRunAllIncremental,
      outputIds,
      flowRunSpecOutputStepParams,
      this.state.shouldSendEmailWhenDone,
      this.state.shouldSendEmailWhenDone ? this.getSubscriptionFlow() : null,
    ).then(result => {
      this.showToastMessages(result);
      this.setState({isRunning: false});
      getAppStore().dispatch(requestSpaceContents());
    });
  }

  private handleEditSchedule = () => {
    this.setState({isRunning: true});
    if (this.props.taskInfo.schedule.id !== this.state.selectedSchedule) {
      RunFlowTasks.setSchedule([this.props.taskInfo.id], this.state.selectedSchedule)
      .then(result => {
        if (result.errors && result.errors.length > 0) {
          this.showErrorMessages(result);
        }
        if (!this.areOutputStepsUnchanged() || !this.areOutputStepsIsIncrementalUnchanged()) {
          this.setOutputSteps();
        } else {
          this.setState({isRunning: false});
          this.closeDialog();
        }
        getAppStore().dispatch(requestSpaceContents());
      });
    } else {
      this.setOutputSteps();
    }
  }

  private setOutputSteps = () => {
    const { outputIds, flowRunSpecOutputStepParams, isRunAllIncremental } = this.getFlowAndOuputStepParams();
    RunFlowTasks.setOutputSteps(this.props.taskInfo.id, this.state.selectAll, isRunAllIncremental, outputIds, flowRunSpecOutputStepParams)
      .then(result => {
        this.showToastMessages(result, true);
        this.setState({ isRunning: false });
      });
  }

  private showToastMessages = (result: IResult, isEdit?: boolean) => {
    if (result.errors && result.errors.length > 0) {
      this.showErrorMessages(result);
    } else {
      this.toasterService.pop({
        type: ToastType.Info,
        text: isEdit ? Messages.editFlowTaskSuccessful() : Messages.createFlowTask(),
        testId: `tb-toast-flow-task-success`
      });
      this.closeDialog();
    }
  }
  private showErrorMessages = (result: IResult) => {
    result.errors.forEach((error: IError) => {
      this.toasterService.pop({
        type: ToastType.Error,
        text: error.message,
        testId: `tb-toast-${error.code}`
      });
    });
  }

  private footerButtons = (state: FlowRunTaskDialogContentState) => (
    <DialogFooterButtonsWidget
      buttons={[{
        handleClick: this.closeDialog,
        buttonType: ButtonType.Outline,
        children: Messages.cancel(),
        testId: 'schedule-flow-run-footer-cancel'
      }, {
        handleClick: this.props.flowInfo ? this.handleCreateSchedule : this.handleEditSchedule,
        buttonType: ButtonType.Go,
        children: this.props.flowInfo ? Messages.scheduleFlowRun() : Messages.editTask(),
        testId: 'schedule-flow-run-footer-go',
        disabled: this.isButtonDisabled()
      }]}
      testId={'schedule-flow-run-footer'}
    />
  )

  private memoizedFooterButtons = defaultMemoize(this.footerButtons);

  // Button is disabled if: Dialog was submitted and waiting results, there is no selected schedules or outputs,
  // if when editing, the selected schedules or outputs haven't changed
  private isButtonDisabled = (): boolean => {
    return this.state.isRunning || !this.state.selectedSchedule || this.areAnyOutputsSelected()
      || (this.props.taskInfo && this.selectedScheduleUnchanged() && this.selectAllUnchanged()
        && this.areOutputStepsUnchanged() && this.areOutputStepsIsIncrementalUnchanged());
  }

  private areAnyOutputsSelected = (): boolean => {
    return !(Object.keys(this.state.outputsSelected).some(key => this.state.outputsSelected[key]));
  }

  private selectedScheduleUnchanged = (): boolean => {
    return this.state.selectedSchedule === this.props.taskInfo.schedule.id;
  }

  private selectAllUnchanged = (): boolean => {
    return this.state.selectAll === this.props.taskInfo.runAllOutputs;
  }

  private areOutputStepsUnchanged = (): boolean => {
    return this.allTaskOutputsSelected() && this.getSelectedOutputsCount() === this.props.taskInfo.flowOutputSteps.length;
  }

  private areOutputStepsIsIncrementalUnchanged = (): boolean => {
    if (this.state.selectAll)
    {
      return (this.state.isRunAllIncremental === this.props.taskInfo.isRunAllIncremental);
    }

    for (const step of this.props.taskInfo.flowOutputSteps) {
      if ((this.state.outputsIsIncremental[step.id] || false) !== (step.isIncremental || false))
        return false;
    }

    return true;
  }

  private allTaskOutputsSelected = (): boolean => {
    return this.props.taskInfo.flowOutputSteps.reduce((acc, step) => {
      return this.state.outputsSelected[step.id] ? acc + 1 : acc;
    }, 0) === this.props.taskInfo.flowOutputSteps.length;
  }

  private getSelectedOutputsCount = (): number => {
    let count = 0;
    Object.keys(this.state.outputsSelected).forEach((output) => this.state.outputsSelected[output] ? count++ : null);
    return count;
  }

  private closeDialog = () => {
    this.props.dismiss && this.props.dismiss();
  }

  private onSubjectChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    this.setState({subjectValue: value});
  }

  private onMessageChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = event.target.value;
    this.setState({messageValue: value});
  }

  private handleShouldSendToMeToggled = () => {
    this.setState({shouldSendToMe: !this.state.shouldSendToMe});
  }

  /**
   * The email form is valid if atleast 1 recipient is selected and the subject is not null or the empty string
   */
  private validateEmailTemplate = () => {
    // TODO we need to ensure we have a valid recipient, so when recipient picker is added, add validation here
    if (!this.state.shouldSendToMe || (this.state.subjectValue === '' || this.state.subjectValue === null)) {
      this.setState({shouldSendEmailWhenDone: false});
    }
  }

  public render(): JSX.Element {
    return (
      <div className={outerMargin}>
        <div className={scrollWrapper}>
          <div className={rootClass}>
            <div className={dialogMessage}>{Messages.scheduleFlowChooseScheduleMessage(
              { FLOW_NAME: L10n.elideIfLong(this.props.flowInfo ? this.props.flowInfo.name : this.props.taskInfo.flow.name) })}</div>
            <FlowRunSchedulePicker
              scheduleId={this.state.selectedSchedule}
              onSelect={this.onSelectSchedule}
            />
          </div>
          <FlowOutputsSelection
            outputs={this.state.outputs}
            selectAll={this.state.selectAll}
            outputsSelected={this.state.outputsSelected}
            outputsIsIncremental={this.state.outputsIsIncremental}
            isRunAllIncremental={this.state.isRunAllIncremental}
            handleChildChange={this.handleOutputChange}
            handleChildIsIncrementalChange={this.handleOutputIsIncrementalChange}
            toggleIncludeAll={this.handleIncludeAll}
            toggleSelectAll={this.toggleSelectAll}
          />
          { FeatureFlags.flowOutputSubscriptionsV1Enabled &&
            <div className={emailWhenDoneWrapper}>
              <CheckBoxWithLabelWidget
                label={Messages.flowSubscriptionsCheckboxLabel()}
                checked={this.state.shouldSendEmailWhenDone}
                handleChange={this.handleEmailWhenDoneToggled}
                testId={'flow-subscription-checkbox-test-id'}
              />
              <FlowSubscriptionButtonWithDialog
                shouldSendToMe={this.state.shouldSendToMe}
                subjectValue={this.state.subjectValue}
                messageValue={this.state.messageValue}
                onShouldSendToMeToggled={this.handleShouldSendToMeToggled}
                onSubjectChange={this.onSubjectChange}
                onMessageChange={this.onMessageChange}
                shouldSendEmailWhenDone={this.state.shouldSendEmailWhenDone}
                onDialogClose={this.validateEmailTemplate}
              />
            </div>
          }
        </div>
        <div className={footerWrapper}>
          {this.memoizedFooterButtons(this.state)}
        </div>
      </div>
    );
  }
}
